import type { CompanyResponseDto } from '@/services/generated/api';
import { formatRut } from '@/lib/rut';
import type { CompanyOption } from './company-select';

/**
 * `CompanyResponseDto` (what `GET /api/companies` returns) -> `CompanyOption`
 * (what `CompanySelect` and `deriveCompanyEnvironment` consume).
 *
 * It exists as a single module because three screens were solving this
 * conversion on their own — `DtesListPage`, `CompaniesListPage` and the shell's
 * identity strip — each with a comment of its own explaining the same thing.
 *
 * And that is where having it in one place paid off. `CompanyResponseDto` did
 * not expose `isSandbox` (gap D12), so this module sent an explicit `false` and
 * a sandbox company showed up as «Certificación» in the issuing filters and in
 * the company listing. When the backend added the field, the fix was one line
 * and all three screens became correct at once; with three copies, whichever
 * one was forgotten would still be lying about the environment — and the
 * environment is what separates a test from a consumed folio.
 *
 * The two booleans travel together on purpose: `deriveCompanyEnvironment` needs
 * both in order to tell `sandbox` from `certification`, and with only one the
 * derivation cannot be complete.
 */
export function toCompanyOptions(
  companies: readonly CompanyResponseDto[] | undefined,
): readonly CompanyOption[] {
  return (companies ?? []).map((company) => ({
    id: company.id,
    legalName: company.legalName,
    taxNumber: company.taxNumber,
    isProduction: company.isProduction,
    isSandbox: company.isSandbox,
  }));
}

/** The separator between the parts of a company label. */
export const COMPANY_LABEL_SEPARATOR = ' · ';

/**
 * How a company is NAMED on screen: «<razón social> · 76.304.364-9».
 *
 * Both together and always, because two companies in the same group have nearly
 * identical legal names and the RUT is the only thing telling them apart
 * unambiguously — the same criterion `CompanySelect` builds its label with,
 * which composes this helper and adds the environment on top only where no
 * `EnvironmentBadge` already says it.
 *
 * ONE format, built here only: the screens used to mix «X · RUT 76.304.364-9 ·
 * Certificación», «X · RUT …» and the bare name. The RUT goes without a «RUT»
 * prefix: dotted and with its check digit it cannot be read as anything else.
 *
 * It is also used by any screen holding a bare `companyId` that needs to say
 * whose it is: a UUID in someone's face does not name a company.
 */
export function companyDisplayName(company: Pick<CompanyOption, 'legalName' | 'taxNumber'>): string {
  return `${companyNameForDisplay(company)}${COMPANY_LABEL_SEPARATOR}${formatRut(company.taxNumber, 'display')}`;
}

/**
 * The razón social as it should be PAINTED next to its own RUT: without a
 * trailing copy of that RUT.
 *
 * Sandbox companies were created with a generated name, «Sandbox 99632700-0»,
 * so the label built above came out as «Sandbox 99632700-0 · 99.632.700-0»:
 * the same RUT twice, once raw and once dotted, eating the width the strip
 * truncates first. Rather than rewrite stored names, the display drops a RUT
 * the name merely repeats — raw or dotted, plus the separator before it —
 * since the formatted RUT is always shown right beside it anyway.
 *
 * It never returns an empty string: a name that IS only its RUT is left
 * untouched, because some name has to be shown.
 */
export function companyNameForDisplay(
  company: Pick<CompanyOption, 'legalName' | 'taxNumber'>,
): string {
  const name = company.legalName.trim();
  const candidates = [
    formatRut(company.taxNumber, 'plain'),
    formatRut(company.taxNumber, 'display'),
    company.taxNumber.trim(),
  ];
  for (const rut of candidates) {
    if (rut.length === 0 || !name.toUpperCase().endsWith(rut.toUpperCase())) continue;
    const stripped = name.slice(0, name.length - rut.length).replace(/[\s·,\-–—]+$/u, '');
    if (stripped.length > 0) return stripped;
  }
  return name;
}
